/**
 * Special (sale) price date-window gating.
 *
 * A product's `special_price` only applies between `special_from` and
 * `special_to` (either bound may be null = open-ended). The PDP, JSON-LD,
 * checkout pricing, and HubSpot sync all enforce this window — listing
 * surfaces must use these helpers so an expired sale never shows a
 * discounted price the PDP/cart won't honor.
 */

type DateInput = Date | string | null | undefined;

function toDate(value: DateInput): Date | null {
  if (!value) return null;
  return value instanceof Date ? value : new Date(value);
}

export function isSpecialPriceActive(
  from: DateInput,
  to: DateInput,
  now: Date = new Date()
): boolean {
  const fromDate = toDate(from);
  const toDateValue = toDate(to);
  return (!fromDate || now >= fromDate) && (!toDateValue || now <= toDateValue);
}

export interface SpecialPriceFields {
  basePrice: string;
  specialPrice: string | null;
  specialFrom: DateInput;
  specialTo: DateInput;
}

/**
 * Returns the special price only when it is a real, currently-running discount:
 * inside its date window and actually below base price. Several rows carry a
 * special price equal to base (SCW-C104/C106 at $9.99), which would otherwise
 * render as a strikethrough of the same number.
 */
export function getActiveSpecialPrice(
  product: SpecialPriceFields,
  now: Date = new Date()
): string | null {
  if (!product.specialPrice) return null;
  if (!isSpecialPriceActive(product.specialFrom, product.specialTo, now)) return null;

  const special = Number.parseFloat(product.specialPrice);
  const base = Number.parseFloat(product.basePrice);
  if (!Number.isFinite(special) || !Number.isFinite(base) || special >= base) return null;

  return product.specialPrice;
}
